import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@stockline/ui/components/popover";
import { Button } from "@stockline/ui/components/button";
import { AlertTriangle, Loader2, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import type {
  PreOrder,
  PreOrderFlow,
  CommentDropDetail,
  PartialBatchLineOutcome,
} from "@/app/api/types/preOrder";

/**
 * Per-id retry-in-flight tracker accepted by `isRetryingExport`. A plain
 * boolean is still supported for legacy call sites, but the popover
 * prefers a Set keyed by `preOrderId` so a cross-navigation race (open
 * popover for group A → click Retry → navigate to group B → click Retry
 * before A settles) shows the spinner ONLY on the specific orders being
 * retried, not on every popover instance in the document.
 *
 * The full single-flight guard lives one layer deeper, in
 * `useConfirmPreOrder` — the hook's `useRef<Set<string>>` rejects the
 * duplicate POST regardless of which popover triggered it. This prop is
 * the UX mirror of that guard.
 */
export type RetryInFlight = boolean | ReadonlySet<string>;

/** Resolve orphan flows out of the cached pre-order using ids written by the
 * backend's sync poll. Safe when `flows` is partial — unmatched ids are
 * dropped from the list, the count still rendered as a fallback row.
 * Returns empty when the reason isn't `orphan_flows` (the other reasons
 * don't carry per-flow context). */
function orphanFlowsOf(po: PreOrder): PreOrderFlow[] {
  const sh = po.pre_order_metadata?.sync_health;
  if (!sh || sh.ill !== true) return [];
  if (sh.reason !== "orphan_flows") return [];
  const ids = new Set(sh.orphan_flow_ids ?? []);
  if (ids.size === 0) return [];
  return (po.flows ?? []).filter((f) => ids.has(f.id));
}

/** Resolve flows behind `partial_batch.failed_flow_ids` for inline rendering.
 * Same safety behaviour as `orphanFlowsOf` — unmatched ids drop out, the
 * count fallback row is rendered. */
function partialBatchFlowsOf(po: PreOrder): {
  flows: PreOrderFlow[];
  outcomes: PartialBatchLineOutcome[];
  failedCount: number;
} {
  const sh = po.pre_order_metadata?.sync_health;
  if (!sh || sh.ill !== true || sh.reason !== "partial_batch") {
    return { flows: [], outcomes: [], failedCount: 0 };
  }
  const ids = new Set(sh.failed_flow_ids ?? []);
  const flows = (po.flows ?? []).filter((f) => ids.has(f.id));
  return {
    flows,
    outcomes: sh.line_outcomes ?? [],
    failedCount: sh.failed_flow_ids?.length ?? 0,
  };
}

/** Resolve comment-drop details for inline rendering. */
function commentDropsOf(po: PreOrder): CommentDropDetail[] {
  const sh = po.pre_order_metadata?.sync_health;
  if (!sh || sh.ill !== true || sh.reason !== "comment_drop") return [];
  return sh.details ?? [];
}

interface IllOrdersPopoverProps {
  illOrders: PreOrder[];
  onHeal: () => void;
  isHealing: boolean;
  /** False when none of the selected ill orders carry a healable reason
   *  (only `orphan_flows` is healable from the UI). When false the heal
   *  button is disabled with an explanatory tooltip; the popover still
   *  surfaces the per-order context so the user knows what to fix. */
  canHeal: boolean;
  /** Async-confirm v1: when an order in the selection has
   * `metadata.export.status === "failed"` the popover surfaces a
   * dedicated "Retry export" CTA wired to a single push-batch call with
   * `intent: "confirm"` and an empty diff (the failure already
   * persisted; we just want to re-fire the export). The parent owns the
   * mutation — the popover just signals the click. Optional so legacy
   * sync-health-only call sites keep working. */
  onRetryExport?: () => void;
  /** Boolean (legacy) or per-id Set (A2 — prevents the cross-popover
   *  race). When a Set is supplied, the spinner+disabled state only
   *  fires when at least one of the popover's failed orders is in the
   *  in-flight set. */
  isRetryingExport?: RetryInFlight;
  children: React.ReactNode;
}

/**
 * Amber popover surfaced on the recap confirm-export button when the ERP
 * sync poll has flagged at least one pre-order as `sync_health.ill === true`.
 *
 * Renders a per-reason summary:
 *  - `orphan_flows`        — lists the orphan flow rows and offers Heal.
 *  - `unresolved_partner`  — shows the client_code; Heal disabled.
 *  - `unresolved_product`  — shows the missing product_codes; Heal disabled.
 *  - `unresolved_employee` — shows the resp_code; Heal disabled.
 *  - `partial_batch`       — lists rejected flows + per-line errors;
 *                            Heal disabled (operator handles via modify).
 *  - `comment_drop`        — lists silently dropped comments; Heal
 *                            disabled (operator re-attaches in M3 UI).
 *  - `divergent`           — lists typed divergence findings; Heal disabled.
 *  - `m3_order_locked`     — shows the lock message; Retry enabled — the
 *                            operator closes the order in M3 and clicks
 *                            Retry, which re-runs the export.
 *
 * `orphan_flows` and `m3_order_locked` are both routed through the same
 * "Réexporter" button — the parent component is in charge of resolving the
 * action, the popover just toggles its enablement based on `canHeal`.
 */
export function IllOrdersPopover({
  illOrders,
  onHeal,
  isHealing,
  canHeal,
  onRetryExport,
  isRetryingExport,
  children,
}: IllOrdersPopoverProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Async-confirm v1: any order whose `metadata.export.status === "failed"`
  // gets a Retry CTA — independent of the sync-health reason because the
  // export-lifecycle failure can co-exist with `ill: false` (the order
  // just hasn't been re-synced yet). Hide the CTA when there's nothing
  // to retry; the parent decides if/when to pass the handler.
  const failedOrderIds = illOrders
    .filter((po) => po.pre_order_metadata?.export?.status === "failed")
    .map((po) => po.id);
  const hasFailedExport = failedOrderIds.length > 0;

  // A2 — per-id retry tracking. When the parent supplies a Set (rather
  // than a bare boolean) the spinner+disabled state only triggers when
  // at least one of THIS popover's failed orders is mid-retry. A boolean
  // keeps legacy behaviour (one global retry state for the component).
  const retryInFlight: boolean = (() => {
    if (typeof isRetryingExport === "boolean") return isRetryingExport;
    if (!isRetryingExport) return false;
    for (const id of failedOrderIds) {
      if (isRetryingExport.has(id)) return true;
    }
    return false;
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className="w-80 p-3"
        align="start"
        data-testid="recap-ill-orders-popover"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
          <span className="text-sm font-semibold text-amber-800">
            {t("ordersRecap.syncHealthIll", {
              defaultValue: "Données incohérentes avec l'ERP",
            })}
          </span>
        </div>

        <p className="text-xs text-amber-700 mb-2">
          {canHeal
            ? t("ordersRecap.syncHealthIllDescription", {
                defaultValue:
                  "Une ou plusieurs lignes existent dans Stockline mais sont absentes de l'ERP. Cliquez sur Réexporter pour ajouter les lignes manquantes côté ERP.",
              })
            : t("ordersRecap.syncHealthIllDescriptionUnresolvable", {
                defaultValue:
                  "Une ressource référencée par l'ERP est introuvable dans Stockline. Corrigez la donnée dans stkdb avant de pouvoir resynchroniser.",
              })}
        </p>

        <div className="space-y-1.5 mb-3 max-h-40 overflow-y-auto">
          {illOrders.map((po) => {
            const sh = po.pre_order_metadata?.sync_health;
            const reason = sh?.ill === true ? sh.reason : null;
            const orphans = orphanFlowsOf(po);
            const orphanCount =
              sh?.ill === true && sh.reason === "orphan_flows"
                ? (sh.orphan_flow_ids?.length ?? 0)
                : 0;
            return (
              <div
                key={po.id}
                className="text-xs bg-amber-50 rounded px-2 py-1.5 border border-amber-100"
                data-testid="recap-ill-orders-row"
                data-preorder-id={po.id}
                data-reason={reason ?? ""}
              >
                <span className="font-medium text-amber-800">
                  {po.pre_order_number || po.partner?.name || po.id}
                </span>
                {reason === "orphan_flows" &&
                  (orphans.length === 0 ? (
                    <p className="text-amber-700 mt-0.5">
                      {t("ordersRecap.syncHealthOrphanCount", {
                        defaultValue: "{{count}} ligne(s) orpheline(s)",
                        count: orphanCount,
                      })}
                    </p>
                  ) : (
                    <ul className="text-amber-700 mt-0.5 space-y-0.5 font-mono">
                      {orphans.map((f) => (
                        <li key={f.id}>
                          {`· ${f.product?.code ?? "?"}`}
                          {f.product?.name ? ` — ${f.product.name}` : ""}
                          {f.weight != null ? ` ${f.weight}kg` : ""}
                          {f.quantity != null && Number(f.quantity) > 0
                            ? ` × ${f.quantity}`
                            : ""}
                        </li>
                      ))}
                    </ul>
                  ))}
                {reason === "unresolved_partner" &&
                  sh?.ill === true &&
                  sh.reason === "unresolved_partner" && (
                    <p className="text-amber-700 mt-0.5 font-mono">
                      {t("ordersRecap.syncHealthUnresolvedPartner", {
                        defaultValue: "Code client introuvable: {{code}}",
                        code: sh.client_code,
                      })}
                    </p>
                  )}
                {reason === "unresolved_product" &&
                  sh?.ill === true &&
                  sh.reason === "unresolved_product" && (
                    <p className="text-amber-700 mt-0.5 font-mono">
                      {t("ordersRecap.syncHealthUnresolvedProduct", {
                        defaultValue: "Produit(s) introuvable(s): {{codes}}",
                        codes: (sh.product_codes ?? []).join(", "),
                      })}
                    </p>
                  )}
                {reason === "unresolved_employee" &&
                  sh?.ill === true &&
                  sh.reason === "unresolved_employee" && (
                    <p className="text-amber-700 mt-0.5 font-mono">
                      {t("ordersRecap.syncHealthUnresolvedEmployee", {
                        defaultValue: "Code responsable introuvable: {{code}}",
                        code: sh.resp_code,
                      })}
                    </p>
                  )}
                {reason === "divergent" &&
                  sh?.ill === true &&
                  sh.reason === "divergent" && (
                    <ul
                      className="text-amber-700 mt-0.5 space-y-0.5 font-mono"
                      data-testid="recap-divergence-list"
                    >
                      {(sh.details ?? []).map((d, i) => (
                        <li
                          key={`${d.kind}-${d.ponr ?? "_"}-${d.flow_id ?? "_"}-${i}`}
                          data-divergence-kind={d.kind}
                        >
                          {`· ${d.kind}`}
                          {d.ponr != null ? ` PONR=${d.ponr}` : ""}
                          {d.expected != null
                            ? ` expected=${String(d.expected)}`
                            : ""}
                          {d.actual != null
                            ? ` actual=${String(d.actual)}`
                            : ""}
                        </li>
                      ))}
                    </ul>
                  )}
                {reason === "m3_order_locked" && (
                  <p
                    className="text-amber-700 mt-0.5"
                    data-testid="recap-ill-orders-locked-message"
                  >
                    {t("ordersRecap.syncHealthM3OrderLocked", {
                      defaultValue:
                        "Commande ouverte dans M3 — fermez-la avant de modifier.",
                    })}
                  </p>
                )}
                {reason === "partial_batch" &&
                  (() => {
                    const { flows, outcomes, failedCount } =
                      partialBatchFlowsOf(po);
                    return (
                      <div
                        className="text-amber-700 mt-0.5"
                        data-testid="recap-partial-batch-block"
                      >
                        <p>
                          {t("ordersRecap.syncHealthPartialBatchCount", {
                            defaultValue:
                              "{{count}} ligne(s) refusée(s) par M3",
                            count: failedCount,
                          })}
                        </p>
                        {flows.length > 0 && (
                          <ul
                            className="mt-0.5 space-y-0.5 font-mono"
                            data-testid="recap-partial-batch-list"
                          >
                            {flows.map((f) => (
                              <li key={f.id} data-flow-id={f.id}>
                                {`· ${f.product?.code ?? "?"}`}
                                {f.product?.name ? ` — ${f.product.name}` : ""}
                                {f.weight != null ? ` ${f.weight}kg` : ""}
                              </li>
                            ))}
                          </ul>
                        )}
                        {outcomes.length > 0 && (
                          <ul
                            className="mt-0.5 space-y-0.5 font-mono text-[10px]"
                            data-testid="recap-partial-batch-errors"
                          >
                            {outcomes
                              .filter((o) => o.status === "rejected")
                              .map((o, i) => (
                                <li
                                  key={`${o.flow_id ?? "_"}-${o.line_index ?? "_"}-${i}`}
                                >
                                  {`· ${o.error ?? "—"}`}
                                </li>
                              ))}
                          </ul>
                        )}
                      </div>
                    );
                  })()}
                {reason === "comment_drop" &&
                  (() => {
                    const drops = commentDropsOf(po);
                    return (
                      <div
                        className="text-amber-700 mt-0.5"
                        data-testid="recap-comment-drop-block"
                      >
                        <p>
                          {t("ordersRecap.syncHealthCommentDrop", {
                            defaultValue:
                              "M3 a ignoré {{count}} commentaire(s) — ré-attachez-les dans M3.",
                            count: drops.length,
                          })}
                        </p>
                        {drops.length > 0 && (
                          <ul
                            className="mt-0.5 space-y-0.5 font-mono"
                            data-testid="recap-comment-drop-list"
                          >
                            {drops.map((d, i) => (
                              <li
                                key={`${d.flow_id ?? "_"}-${d.ponr ?? "_"}-${i}`}
                              >
                                {`· `}
                                {d.ponr != null ? `PONR=${d.ponr} ` : ""}
                                {d.field ? `${d.field}: ` : ""}
                                {d.comment ?? "—"}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })()}
              </div>
            );
          })}
        </div>

        <Button
          variant="outline"
          size="sm"
          className="w-full h-7 text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
          data-testid="recap-ill-orders-heal-button"
          title={
            !canHeal
              ? t("ordersRecap.syncHealthHealDisabledHint", {
                  defaultValue:
                    "Cette incohérence doit être résolue dans stkdb avant de pouvoir resynchroniser.",
                })
              : undefined
          }
          onClick={(e) => {
            e.stopPropagation();
            if (!canHeal) return;
            onHeal();
            setOpen(false);
          }}
          disabled={isHealing || !canHeal}
        >
          {isHealing ? (
            <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
          ) : (
            <RotateCcw className="h-3 w-3 mr-1.5" />
          )}
          {isHealing
            ? t("ordersRecap.syncHealthHealing", {
                defaultValue: "Réexport en cours…",
              })
            : canHeal
              ? t("ordersRecap.syncHealthHeal", {
                  defaultValue: "Réexporter les lignes manquantes",
                })
              : t("ordersRecap.syncHealthHealUnavailable", {
                  defaultValue: "Résolvez d'abord dans stkdb",
                })}
        </Button>
        {/* Async-confirm v1 — dedicated Retry export CTA. Shows up when
            any order in the selection has `metadata.export.status ===
            "failed"` and the parent has wired the handler. The retry
            fires push-batch with intent:"confirm" + empty diff so the
            server re-stamps `in_progress` and the ConfirmConsumer
            picks the row up again. Independent of the heal flow above
            so each failure mode keeps its own click target. */}
        {hasFailedExport && onRetryExport ? (
          <Button
            variant="outline"
            size="sm"
            className="w-full h-7 text-xs mt-1.5 border-red-300 text-red-700 hover:bg-red-50"
            data-testid="recap-ill-orders-retry-export-button"
            onClick={(e) => {
              e.stopPropagation();
              onRetryExport();
              setOpen(false);
            }}
            disabled={retryInFlight}
          >
            {retryInFlight ? (
              <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
            ) : (
              <RotateCcw className="h-3 w-3 mr-1.5" />
            )}
            {retryInFlight
              ? t("ordersRecap.retryExportInFlight", {
                  defaultValue: "Re-export en cours…",
                })
              : t("ordersRecap.retryExport", { defaultValue: "Retry export" })}
          </Button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
